import moment from 'moment';
import { PropTypes } from 'prop-types';
import { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import AccionaTableTabs from '../../../../../components/AccionaTableTabs';
import RemoveModal from '../../../../../components/RemoveModal/index';
import { MainContext } from '../../../../../context/MainContext';
import { getScannedDocumentByMysqlIDAndPid } from '../../../../../services/api/scannedDocuments';
import * as noValidatedRCScannedDocumentsActions from '../../../../../services/redux/scannedDocumentsRC/noValidatedRCScannedDocuments/actions';
import * as validatedRCScannedDocumentsActions from '../../../../../services/redux/scannedDocumentsRC/validatedRCScannedDocuments/actions';
import {
  BUTTONS_NO_VALIDATED,
  BUTTONS_VALIDATED,
  COLUMNS_HEADERS_VALIDATED,
  getNoValidatedHeadersColumns,
} from './columns';
import AccionaIcon from '../../../../../components/AccionaIcon';
import styles from './index.module.scss';
import AccionaLabel from '../../../../../components/AccionaLabel';
import { Providers } from '../../../../../constants/providers';
import { getCemosaUpdates } from '../../../../../services/api/cemosa';
import { List, message } from 'antd';
import Modal from '../../../../../components/Modal';
import useDownloadXlsxFile from '../hooks/useDownLoadXlsxFile';

const Tabs = {
  VALIDATED: 'validated',
  NO_VALIDATED: 'noValidated',
};

const { labTr } = styles;

const ScannedDocumentsList = ({
  getValidatedScannedDocumentsList,
  getNoValidatedScannedDocumentsList,
  deleteValidatedScannedDocument,
  deleteNoValidatedScannedDocument,
  errorMessages,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { idDetail: id, id: pid, validated } = useParams();
  const { project } = useOutletContext();
  const [initialDocketFilter, setInitialDocketFilter] = useState(null);
  const [hasAppliedInitialFilter, setHasAppliedInitialFilter] = useState(false);
  const { setLoading, setError } = useContext(MainContext);
  const [selectedTab, setSelectedTab] = useState(validated);
  const [loadingScannedDocumentByMysql, seLoadingScannedDocumentByMysql] =
    useState(Boolean(id));
  const [
    validatedTableFiltersAndPagination,
    setValidatedTableFiltersAndPagination,
  ] = useState({});
  const { me } = useSelector(({ auth }) => auth);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [cemosaUpdates, setCemosaUpdates] = useState();
  const [fetchingCemosaUpdates, setFetchingCemosaUpdates] = useState(false);
  const { loading, downloadXlsx } = useDownloadXlsxFile(project);
  const initialFilters = useMemo(
    () =>
      !hasAppliedInitialFilter && initialDocketFilter
        ? { docket_number: initialDocketFilter, sortable: 'docket_number-ASC' }
        : undefined,
    [initialDocketFilter, hasAppliedInitialFilter],
  );

  const initialFiltersNotValidated = useMemo(
    () => ({ sortable: 'fileName-ASC' }),
    [],
  );

  const handleDownloadXlsx = async () => {
    downloadXlsx();
  };

  const onClickDeleteIcon = (scannedDocument) => {
    setSelectedRows([scannedDocument]);
    setShowRemoveModal(true);
  };
  const onClickEditIcon = (scannedDocument, isValidated) => {
    navigate(
      `/app/project/${
        project.id_project
      }/quality/ocr/scannedDocuments/scannedDocumentsRC/update/${
        scannedDocument.model
      }/${scannedDocument.id}/${scannedDocument.fileName}${
        scannedDocument.model === 'delivery' ? `/${selectedTab}` : ''
      }?${isValidated ? 'validated' : 'noValidated'}`,
    );
  };

  const onClickLotifiedAndAssayedEditIcon = (scannedDocument, isValidated) => {
    navigate(
      `/app/project/${
        project.id_project
      }/quality/ocr/scannedDocuments/scannedDocumentsRC/update/${
        scannedDocument.model
      }/${scannedDocument.id}/${scannedDocument.fileName}${
        scannedDocument.model === 'delivery' ? `/${selectedTab}` : ''
      }?${isValidated ? 'validated' : 'noValidated'}&assayedAndLotified=true`,
    );
  };
  const onClickDataIcon = (scannedDocument) =>
    navigate(
      `/app/project/${project.id_project}/quality/ocr/scannedDocuments/scannedDocumentsRC/document/${scannedDocument.model}/${scannedDocument.id}/${scannedDocument.mysqlId}/${scannedDocument.fileName}`,
    );

  const onClickEheIcon = (scannedDocument) => {
    navigate(
      `/app/project/${pid}/quality/normative/${
        project.normative_data?.name?.toLowerCase() === 'ehe' ? 'ehe' : 'coe'
      }/traceability?did=${scannedDocument.docket_number}&plant=${
        scannedDocument.delivery_note_data.plant
      }&supplier_id=${scannedDocument.supplier}`,
    );
  };
  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    if (selectedTab === 'validated') {
      deleteValidatedScannedDocument(selectedRows);
    } else {
      deleteNoValidatedScannedDocument(selectedRows);
    }
    setSelectedRows([]);
  };

  const handleOnCancelCemosaUpdatesModal = () => {
    setCemosaUpdates(null);
    const { pageSize, current } = validatedTableFiltersAndPagination.pagination;
    dispatch(
      validatedRCScannedDocumentsActions.getValidatedScannedDocuments({
        size: pageSize,
        page: current,
        filters: {
          ...validatedTableFiltersAndPagination.filters,
          pid: project.id_project,
        },
      }),
    );
  };

  const onTabChange = (tab) => {
    setSelectedTab(tab);
    setSelectedRows([]);
    navigate(`../${tab}`);
  };

  const isCEMOSAProvider = useMemo(() => {
    return project?.laboratory_providers.some(
      ({ provider }) => provider === Providers.CEMOSA,
    );
  }, [project]);

  const handleOnValidatedChange = useCallback(
    (fitersAndPagination) => {
      setValidatedTableFiltersAndPagination(fitersAndPagination);
      if (!hasAppliedInitialFilter) {
        setHasAppliedInitialFilter(true);
      }
    },
    [hasAppliedInitialFilter],
  );

  const handleOnFetchCemosaUpdates = async () => {
    setFetchingCemosaUpdates(true);
    getCemosaUpdates(project.id_project)
      .then((updatedScannedDocuments) => {
        if (updatedScannedDocuments?.data?.updated.length) {
          setCemosaUpdates(updatedScannedDocuments.data.updated);
        } else {
          message.info(
            intl.formatMessage({
              id: 'pages.quality.ocr.scannedDocuments.cemosa.noUpdates',
            }),
          );
        }
      })
      .catch((error) => {
        message.error(
          intl.formatMessage({
            id: 'pages.quality.ocr.scannedDocuments.cemosa.updates.error',
          }),
        );
      })
      .finally(() => {
        setFetchingCemosaUpdates(false);
      });
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [selectedTab]);

  useEffect(() => {
    if (errorMessages) {
      setError({
        status: 500,
        errorType: 'GET_SCANNED_DOCUMENT_ERROR',
      });
    }
  }, [errorMessages, setError]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      seLoadingScannedDocumentByMysql(true);
      const [sdData] = (
        await getScannedDocumentByMysqlIDAndPid(id, pid, 'delivery')
      ).data.data;
      if (sdData) {
        setInitialDocketFilter(sdData.docket_number);
      }
      setLoading(false);
      seLoadingScannedDocumentByMysql(false);
    };
    if (id) {
      fetchData().catch((error) => {
        setLoading(false);
        setError({
          status: 500,
          errorType: 'GET_SCANNED_DOCUMENT_ERROR',
        });
      });
    }
  }, [id, navigate, setError, setLoading, pid]);

  if (id && loadingScannedDocumentByMysql) {
    return null;
  }

  return (
    <>
      <AccionaTableTabs
        selectedTab={selectedTab}
        setSelectedTab={onTabChange}
        tabs={[Tabs.VALIDATED, Tabs.NO_VALIDATED]}
        title={intl.formatMessage({
          id: 'pages.quality.ocr.scannedDocuments.scannedDocumentsRC',
        })}
        actions={
          <>
            {isCEMOSAProvider && selectedTab === Tabs.VALIDATED && (
              <AccionaButton
                id="createBtn"
                onClick={handleOnFetchCemosaUpdates}
                msg={intl.formatMessage({
                  id: 'pages.quality.ocr.scannedDocuments.cemosa.updates.button',
                })}
                classIcon="icon fas fa-sync"
                type="white"
                loading={fetchingCemosaUpdates}
              />
            )}
            {selectedTab === Tabs.VALIDATED && (
              <AccionaButton
                id="downloadXlsxBtn"
                onClick={handleDownloadXlsx}
                msg="XLSX"
                classIcon="icon fas fa-download"
                type="white"
                loading={loading}
              />
            )}
            <AccionaButton
              id="deleteBtn"
              disabled={!selectedRows.length || !me?.is_active}
              onClick={() => {
                setShowRemoveModal(true);
              }}
              msg={intl.formatMessage({ id: 'button.delete' })}
              classIcon="icon fas fa-trash-alt"
            />
          </>
        }
        render={() => {
          switch (selectedTab) {
            case Tabs.VALIDATED:
              return (
                <AccionaTable
                  showTitle={false}
                  setLoading={setLoading}
                  allowClear
                  allowClearStyle={{
                    position: 'absolute',
                    top: -61,
                    right: 15,
                  }}
                  onChange={handleOnValidatedChange}
                  onClearFilter={() => setHasAppliedInitialFilter(true)}
                  initialFilters={initialFilters}
                  key={Tabs.VALIDATED}
                  columns={COLUMNS_HEADERS_VALIDATED}
                  getItems={getValidatedScannedDocumentsList}
                  entity="validatedRCScannedDocuments"
                  expandable={{
                    expandedRowRender: (record) => {
                      return (
                        <tr className={labTr}>
                          <td />
                          <td />
                          <td>{record.laboratoryNoteSD.supplier}</td>
                          <td>{record.laboratoryNoteSD.arriving_date}</td>
                          <td>
                            <AccionaIcon
                              className="btn btn-darkgray"
                              //onClick={() => viewAction(x[columns.collapsable])}
                              classIcon="icon fas fa-eye"
                              toolTip={intl.formatMessage({
                                id: BUTTONS_VALIDATED.viewAction.tooltip,
                              })}
                              onClick={() =>
                                onClickDataIcon(record.laboratoryNoteSD)
                              }
                            />
                          </td>
                          <td />
                        </tr>
                      );
                    },
                    rowExpandable: (record) => record.laboratoryNoteSD,
                  }}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  isSelectionDisabled={(record) => {
                    return record.isLotified || record.isAssayed;
                  }}
                  isRowChecked={(record) => {
                    return selectedRows.some((r) => r.id === record.id);
                  }}
                  actions={(record) => {
                    return (
                      <>
                        <AccionaTableButton
                          onClick={() => onClickDataIcon(record)}
                          type="view"
                          toolTip={intl.formatMessage({
                            id: BUTTONS_VALIDATED.viewAction.tooltip,
                          })}
                        />
                        <AccionaTableButton
                          onClick={() => onClickEheIcon(record)}
                          type="link"
                          toolTip={intl.formatMessage({
                            id: `${
                              project.id_normative === 1 ? 'ehe' : 'coe'
                            }Action.record`,
                          })}
                        />
                        {!record.isLotified && !record.isAssayed && (
                          <AccionaTableButton
                            onClick={() => onClickDeleteIcon(record)}
                            type="delete"
                            disabled={!me?.is_active}
                            toolTip={intl.formatMessage({
                              id: BUTTONS_VALIDATED.deleteAction.tooltip,
                            })}
                          />
                        )}
                        {!record.isLotified && !record.isAssayed && (
                          <AccionaTableButton
                            onClick={() => onClickEditIcon(record, true)}
                            type="edit"
                            disabled={!me?.is_active}
                            toolTip={intl.formatMessage({
                              id: BUTTONS_VALIDATED.editAction.tooltip,
                            })}
                          />
                        )}
                        {(record.isLotified || record.isAssayed) && (
                          <AccionaTableButton
                            onClick={() =>
                              onClickLotifiedAndAssayedEditIcon(record, true)
                            }
                            type="edit"
                            disabled={!me?.is_active}
                            toolTip={intl.formatMessage({
                              id: BUTTONS_VALIDATED.editAction.tooltip,
                            })}
                          />
                        )}
                        {record.isLotified || record.isAssayed ? (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              flexDirection: 'column',
                            }}
                          >
                            {record.isLotified && (
                              <AccionaLabel>
                                {intl.formatMessage({ id: 'lotified' })}
                              </AccionaLabel>
                            )}
                            {record.isAssayed && (
                              <AccionaLabel>
                                {intl.formatMessage({ id: 'assayed' })}
                              </AccionaLabel>
                            )}
                          </div>
                        ) : null}
                      </>
                    );
                  }}
                />
              );
            case Tabs.NO_VALIDATED:
              return (
                <AccionaTable
                  key={Tabs.NO_VALIDATED}
                  columns={getNoValidatedHeadersColumns(intl)}
                  initialFilters={initialFiltersNotValidated}
                  getItems={getNoValidatedScannedDocumentsList}
                  entity="noValidatedRCScannedDocuments"
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  showTitle={false}
                  dataParseFunction={(nv) => {
                    return nv.map((e) => ({
                      ...e,
                      createdAt: moment(e.createdAt, 'DD-MM-YYYY'),
                    }));
                  }}
                  actions={(nv) => (
                    <>
                      <AccionaTableButton
                        onClick={() => onClickEditIcon(nv, false)}
                        type="edit"
                        disabled={!me?.is_active}
                        toolTip={intl.formatMessage({
                          id: BUTTONS_NO_VALIDATED.editAction.tooltip,
                        })}
                      />
                      <AccionaTableButton
                        onClick={() => onClickDeleteIcon(nv)}
                        type="delete"
                        disabled={!me?.is_active}
                        toolTip={intl.formatMessage({
                          id: BUTTONS_NO_VALIDATED.deleteAction.tooltip,
                        })}
                      />
                    </>
                  )}
                />
              );
            default:
              return null;
          }
        }}
      />
      <RemoveModal
        entity="scannedDocuments"
        selectedRows={selectedRows}
        msg={intl.formatMessage({
          id:
            selectedRows.length > 1
              ? 'delete.scannedDocuments'
              : 'delete.scannedDocument',
        })}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
      <Modal
        title={intl
          .formatMessage({
            id: 'pages.quality.ocr.scannedDocuments.cemosa.updates.modal.title',
          })
          .toLocaleUpperCase()}
        open={!!cemosaUpdates}
        onCancel={handleOnCancelCemosaUpdatesModal}
        width="40vw"
        closable
      >
        <div style={{ padding: '10px 20px' }}>
          <p>
            {intl.formatMessage({
              id: 'pages.quality.ocr.scannedDocuments.cemosa.updates.modal.message',
            })}
          </p>
          <List size="small">
            {cemosaUpdates?.map((update) => (
              <List.Item key={update}>{update}</List.Item>
            ))}
          </List>
          <div style={{ marginTop: '10px', textAlign: 'center' }}>
            <AccionaButton
              type="primary"
              onClick={handleOnCancelCemosaUpdatesModal}
              msg={intl.formatMessage({
                id: 'pages.quality.ocr.scannedDocuments.cemosa.updates.modal.ok.button',
              })}
            ></AccionaButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = ({
  validatedRCScannedDocuments,
  noValidatedRCScannedDocuments,
}) => ({
  errorMessages:
    validatedRCScannedDocuments.errorMessages ||
    noValidatedRCScannedDocuments.errorMessages,
});

const matchDispachToProps = {
  getValidatedScannedDocumentsList:
    validatedRCScannedDocumentsActions.getValidatedScannedDocuments,
  getNoValidatedScannedDocumentsList:
    noValidatedRCScannedDocumentsActions.getNoValidatedScannedDocuments,
  deleteNoValidatedScannedDocument:
    noValidatedRCScannedDocumentsActions.deleteNoValidatedScannedDocument,
  deleteValidatedScannedDocument:
    validatedRCScannedDocumentsActions.deleteValidatedScannedDocument,
};

ScannedDocumentsList.defaultProps = {
  errorMessages: {},
  selectedRows: [],
};

ScannedDocumentsList.propTypes = {
  getValidatedScannedDocumentsList: PropTypes.func.isRequired,
  getNoValidatedScannedDocumentsList: PropTypes.func.isRequired,
  deleteValidatedScannedDocument: PropTypes.func.isRequired,
  deleteNoValidatedScannedDocument: PropTypes.func.isRequired,
  errorMessages: PropTypes.shape({
    status: PropTypes.number,
  }),
  selectedRows: PropTypes.arrayOf(PropTypes.object),
};

export default connect(
  mapStateToProps,
  matchDispachToProps,
)(injectIntl(ScannedDocumentsList));
